<script setup lang="ts">
import type {
  ColDef,
  ICellRendererParams,
  RowSelectionOptions,
} from "ag-grid-community";
import { storeToRefs } from "pinia";
import { computed } from "vue";
import AppGrid from "../../components/AppGrid.vue";
import { DETAIL_GRID_DEFAULT_COL_DEF } from "../../components/composables/useGridColumnDefaults.ts";
import type { Sku } from "../../redistribution/models/Sku.js";
import { useAppStore } from "../../stores/app.ts";
import {
  calculateUrgency,
  computeDaysToStockout,
  getDailyDemandForRateOfSale,
  stockoutBarColor,
  stockoutBarWidth,
  urgencyLabel,
} from "./resultHelpers.ts";

const DETAIL_GRID_ROW_HEIGHT = 30;
const MAX_DETAIL_VISIBLE_ROWS = 5;

const appStore = useAppStore();
const { rateOfSale } = storeToRefs(appStore);

const properties = defineProps<{
  params: ICellRendererParams;
}>();

interface ItemRow {
  sku: string;
  collection: string;
  description: string;
  color: string;
  qtyOnPo: number;
  cubes: number;
  currentStock: number;
  forecastQuantityAvailable: number;
  dailyDemand: number;
  daysToStockout: number;
  weeksOfStock: number;
  urgency: string;
  urgencyCssClass: string;
  urgencyEmoji: string;
}

// Route ROS through the shared resolver so any selected window (not just
// 30/60/90) resolves to a real or estimated daily demand. The previous
// `Number(sku[roS${rateOfSale}])` had no fallback, so non-30/60/90 windows
// produced NaN -> 0 demand and a bogus "N/A" urgency.
function getDailyDemand(sku: Sku): number {
  return getDailyDemandForRateOfSale(sku, rateOfSale.value);
}

const items = computed<ItemRow[]>(() => {
  const data = properties.params.data as { skus?: Sku[] } | undefined;
  if (!Array.isArray(data?.skus)) {
    return [];
  }

  return data.skus.map((sku) => {
    const dailyDemand = getDailyDemand(sku);
    const daysToStockout = computeDaysToStockout(
      sku.forecastQuantityAvailable,
      dailyDemand
    );
    const urgency = calculateUrgency(daysToStockout, dailyDemand);
    const urgencyLabelText = urgencyLabel(urgency, dailyDemand);
    return {
      sku: sku.skuNumber,
      collection: sku.theme,
      description: sku.description,
      color: sku.color,
      qtyOnPo: sku.lineItemQuantity,
      cubes: sku.cubeSize * sku.lineItemQuantity,
      currentStock: sku.quantityAvailable,
      forecastQuantityAvailable: sku.forecastQuantityAvailable,
      dailyDemand,
      daysToStockout,
      weeksOfStock: Number((daysToStockout / 7).toFixed(1)),
      urgency: urgencyLabelText.text,
      urgencyCssClass: urgencyLabelText.cssClass,
      urgencyEmoji: urgencyLabelText.emoji,
    };
  });
});

const detailGridId = computed(() => {
  const data = properties.params.data as
    | { parentPoNumber?: string }
    | undefined;
  const suffix =
    data?.parentPoNumber?.replaceAll(/[^a-zA-Z0-9_-]/g, "-") ?? "items";
  return `po-detail-grid-${suffix}`;
});

function getUrgencyColors(cssClass: string): {
  background: string;
  color: string;
} {
  switch (cssClass) {
    case "urgency-critical": {
      return { background: "#fdecea", color: "#c0392b" };
    }
    case "urgency-moderate": {
      return { background: "#fff3cd", color: "#856404" };
    }
    case "urgency-na": {
      return { background: "#f0f0f0", color: "#888888" };
    }
    default: {
      return { background: "#eafaf1", color: "#27ae60" };
    }
  }
}

function renderWeeksOfStockCell(parameters: {
  data?: ItemRow;
}): HTMLElement | string {
  if (!parameters.data) {
    return "";
  }

  const wrapper = document.createElement("div");
  wrapper.className = "flex items-center gap-2";

  const track = document.createElement("span");
  track.className = "po-detail-renderer-stockout-track";

  const fill = document.createElement("div");
  fill.className = "po-detail-renderer-stockout-fill";
  fill.style.width = stockoutBarWidth(parameters.data.daysToStockout);
  fill.style.background = stockoutBarColor(parameters.data.daysToStockout);

  track.append(fill);
  wrapper.append(track);

  const label = document.createElement("span");
  label.textContent = `${parameters.data.weeksOfStock.toFixed(1)} weeks`;
  wrapper.append(label);

  return wrapper;
}

function renderUrgencyCell(parameters: {
  data?: ItemRow;
}): HTMLElement | string {
  if (!parameters.data) {
    return "";
  }

  const pill = document.createElement("span");
  pill.className = "po-detail-grid-urgency-pill";

  const colors = getUrgencyColors(parameters.data.urgencyCssClass);
  pill.style.background = colors.background;
  pill.style.color = colors.color;
  pill.textContent = `${parameters.data.urgencyEmoji} ${parameters.data.urgency}`;

  return pill;
}

const detailCellStyle = {
  paddingLeft: "12px",
  paddingRight: "12px",
  fontSize: "12px",
};

const URGENCY_RANK: Record<string, number> = {
  Critical: 3,
  Moderate: 2,
  OK: 1,
  "N/A": 0,
};

const columnDefs: ColDef<ItemRow>[] = [
  {
    headerName: "SKU",
    field: "sku",
    minWidth: 70,
    cellStyle: { ...detailCellStyle, fontWeight: "700" },
  },
  { headerName: "Collection", field: "collection", minWidth: 72 },
  { headerName: "Description", field: "description", minWidth: 120, flex: 2 },
  { headerName: "Color", field: "color", minWidth: 65 },
  { headerName: "Qty on PO", field: "qtyOnPo", minWidth: 65 },
  {
    headerName: "Cubes",
    field: "cubes",
    minWidth: 65,
    valueFormatter: (parameters) => {
      const value = Number(parameters.value);
      return Number.isFinite(value) ? value.toFixed(2) : "";
    },
  },
  { headerName: "Current Stock", field: "currentStock", minWidth: 72 },
  {
    headerName: "Forecast QTY",
    field: "forecastQuantityAvailable",
    minWidth: 72,
  },
  {
    headerName: "Daily Demand",
    field: "dailyDemand",
    minWidth: 72,
    valueFormatter: (parameters) => {
      const value = parameters.value as unknown;
      if (typeof value !== "number" || !Number.isFinite(value)) return "—";
      return `${value.toFixed(2)}/day`;
    },
  },
  {
    headerName: "Weeks of Stock",
    field: "weeksOfStock",
    minWidth: 95,
    cellRenderer: renderWeeksOfStockCell,
  },
  {
    headerName: "Urgency",
    field: "urgency",
    minWidth: 80,
    sort: "desc",
    comparator: (valueA: string, valueB: string, nodeA, nodeB) => {
      const rankA = URGENCY_RANK[valueA] ?? 0;
      const rankB = URGENCY_RANK[valueB] ?? 0;
      if (rankA !== rankB) {
        return rankA - rankB;
      }
      const dtsA = nodeA.data?.daysToStockout ?? 999;
      const dtsB = nodeB.data?.daysToStockout ?? 999;
      return dtsA - dtsB;
    },
    cellRenderer: renderUrgencyCell,
  },
];

const defaultColDef: ColDef = {
  ...DETAIL_GRID_DEFAULT_COL_DEF,
  cellStyle: detailCellStyle,
};

function getDetailRowHeight(): number {
  return DETAIL_GRID_ROW_HEIGHT;
}

const rowSelection: RowSelectionOptions = {
  mode: "singleRow",
  checkboxes: false,
  enableClickSelection: false,
};
</script>

<template>
  <div class="po-detail-renderer-grid min-w-0 overflow-hidden bg-slate-50 p-0">
    <AppGrid
      :id="detailGridId"
      :column-defs="columnDefs"
      :row-data="items"
      :default-col-def="defaultColDef"
      :row-selection="rowSelection"
      dom-layout="normal"
      :get-row-height="getDetailRowHeight"
      :default-row-height="DETAIL_GRID_ROW_HEIGHT"
      :max-rows="MAX_DETAIL_VISIBLE_ROWS"
      :min-grid-height="0"
    />
  </div>
</template>
